import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/apiClient";
import { Sparkles, Mountain, Leaf, ShieldCheck, Package, Bookmark, Hexagon, Network, Database, ShoppingBag, ArrowRight, GitBranch, Beaker, Wind, Layers, Sprout } from "lucide-react";

/**
 * TraitMatrix — honest, visual exploration of cultivar metadata.
 *
 * We never claim DNA sequencing or genomic verification. The Trait Matrix
 * is purely a structured aggregation of the publicly approved lineage
 * record set: classification, seed type, cycle type, landrace/heirloom/
 * preservation flags, strain families, and the top tags observed in
 * recorded lineages.
 */

const CLASS_META = {
  indica: { label: "Indica", color: "#A06CFF" },
  indica_dominant: { label: "Indica-dominant", color: "#B58CFF" },
  hybrid: { label: "Hybrid", color: "#4FA86F" },
  balanced: { label: "Balanced", color: "#7CCB99" },
  sativa_dominant: { label: "Sativa-dominant", color: "#FFC97A" },
  sativa: { label: "Sativa", color: "#FFB454" },
  cbd: { label: "CBD", color: "#5FB4E8" },
  cbg: { label: "CBG", color: "#5FB4E8" },
  high_thc: { label: "High-THC", color: "#FF7A6A" },
  high_cbd: { label: "High-CBD", color: "#5FB4E8" },
  ruderalis: { label: "Ruderalis", color: "#8C8F95" },
  unknown: { label: "Unknown", color: "#6B6E73" },
};

export default function TraitMatrix({ breederId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // v3.99c.40 — Matrix v2 (listing-aware navigation hub). Loaded in parallel
  // with the legacy aggregate stats so the existing layout still renders
  // immediately while the nav hub hydrates separately.
  const [matrix, setMatrix] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    // v3.86 — per-breeder pages pass `breederId` so the matrix only
    // aggregates that breeder's lineage instead of the whole catalog.
    const params = breederId ? { breeder_id: breederId } : {};
    api.get("/lineage/traits", { params })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    // Navigation hub (only fetched on the global matrix, not per-breeder)
    if (!breederId) {
      api.get("/traits/matrix")
        .then((r) => { if (alive) setMatrix(r.data); })
        .catch(() => { if (alive) setMatrix(null); });
    }
    return () => { alive = false; };
  }, [breederId]);

  if (loading) {
    return <div className="bg-[var(--surface)] border border-[var(--border)] p-10 text-center text-sm"
                style={{ color: "var(--text-muted)" }}
                data-testid="trait-matrix-loading">
      Computing the trait matrix…
    </div>;
  }

  if (!data || data.total_public_records === 0) {
    return (
      <div className="bg-[var(--surface)] border border-[var(--border)] p-12 md:p-16 text-center"
           data-testid="trait-matrix-empty">
        <Hexagon className="w-7 h-7 mx-auto mb-4" style={{ color: "var(--gold)" }} />
        <div className="font-display text-2xl md:text-3xl tracking-tighter mb-2" style={{ color: "var(--text)" }}>
          The trait matrix is still being built
        </div>
        <p className="text-sm leading-relaxed max-w-md mx-auto" style={{ color: "var(--text-2)" }}>
          As verified lineage records come in, this matrix will show classifications, families, seed-type spread,
          preservation flags, and the most-recorded cultivar traits. Cultivar metadata only — no claims about lab
          sequencing.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="trait-matrix">
      {/* Headline counter row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Public records mapped" value={data.total_public_records} accent />
        <Stat label="Originator breeders" value={data.originator_breeders_mapped} icon={Network} />
        <Stat label="Listed on CannaForge" value={data.flags.with_listings} icon={Package} accent />
        <Stat label="Collector saves" value={data.saves_total} icon={Bookmark} />
      </div>

      {/* v3.99c.40 — Trait navigation hub. Every chip routes to a real SEO
          landing page (/families/:slug, /terpenes/:slug, …). Commercial-
          intent traits with live listings are visually highlighted. */}
      {matrix && <TraitNavHub matrix={matrix} />}

      {/* Classification wheel */}
      <ClassificationWheel data={data} />

      {/* Two-column trait band */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <BarPanel title="Strain families" icon={Sparkles} items={data.strain_family} unit="records" testid="trait-strain-family" />
        <BarPanel title="Seed type" icon={Leaf} items={data.seed_type} unit="records" testid="trait-seed-type" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <BarPanel title="Cycle type" icon={Leaf} items={data.cycle_type} unit="records" testid="trait-cycle-type" />
        <PreservationCard flags={data.flags} total={data.total_public_records} />
      </div>

      {/* Curated trait buckets — v3.80 */}
      <TraitBuckets data={data} />

      {/* v3.89i — Terpenes panel (sourced from Demarily enrichment + admin entry). */}
      <TerpenePanel data={data} />

      {/* Honest framing footer */}
      <div className="border border-[var(--border)] bg-[var(--surface)] p-4 text-[12px] leading-relaxed"
           style={{ color: "var(--text-muted)" }}
           data-testid="trait-matrix-disclaimer">
        <strong style={{ color: "var(--text-2)" }}>Cultivar metadata only.</strong>{" "}
        Mapped from submitted and verified strain records in the CannaForge Lineage Atlas. Not derived from lab
        sequencing or genomic testing. Counts update as new records are reviewed and approved. Public trait
        buckets are curated — internal admin tags and breeder names are excluded.
      </div>
    </div>
  );
}

/**
 * TraitBuckets — v3.80
 * Curated public-trait surface. Pulls structured `public_trait_buckets`
 * payload from /api/lineage/traits (family / aroma / structure /
 * preservation) instead of dumping every raw tag.
 */
const BUCKET_META = {
  family:        { label: "Genetic families",         Icon: Leaf,        accent: "Curated · CannaForge whitelist", traitType: "families" },
  aroma:         { label: "Aroma & profile",          Icon: Sparkles,    accent: "From recorded notes & lineage text", traitType: "aromas" },
  structure:     { label: "Plant structure",          Icon: Network,     accent: "Morphology cues only", traitType: "structures" },
  preservation:  { label: "Preservation & origin",    Icon: ShieldCheck, accent: "Landrace / Heirloom / Foundation lines", traitType: "preservation" },
};

function _slugify(v) { return (v || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""); }

function TraitBuckets({ data }) {
  const buckets = data.public_trait_buckets || {};
  const order = ["family", "aroma", "structure", "preservation"];
  const nonEmpty = order.filter((k) => (buckets[k] || []).length > 0);
  if (!nonEmpty.length) {
    return (
      <div className="bg-[var(--surface)] border border-[var(--border)] p-8 text-center text-sm"
           style={{ color: "var(--text-muted)" }} data-testid="trait-buckets-empty">
        Curated public traits will surface here as approved records carry recognizable family / aroma / structure / preservation signals.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="trait-buckets">
      {nonEmpty.map((k) => {
        const meta = BUCKET_META[k];
        const items = buckets[k] || [];
        const max = Math.max(1, ...items.map((x) => x.count));
        return (
          <div key={k} className="bg-[var(--surface)] border border-[var(--border)] p-5" data-testid={`trait-bucket-${k}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="label-eyebrow flex items-center gap-2" style={{ color: "var(--text-muted)" }}>
                {meta.Icon && <meta.Icon className="w-3 h-3" style={{ color: "var(--gold)" }} />} {meta.label}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-[0.16em]" style={{ color: "var(--text-muted)" }}>
                {meta.accent}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {items.map((it) => {
                const ratio = it.count / max;
                const slug = _slugify(it.value);
                return (
                  <Link key={it.value}
                        to={`/${meta.traitType}/${slug}`}
                        className="inline-flex items-baseline gap-1.5 px-3 py-1.5 border transition-colors hover:bg-[var(--bg-2)]"
                        style={{
                          borderColor: ratio >= 0.6 ? "var(--gold)" : "var(--border)",
                          color: ratio >= 0.6 ? "var(--gold)" : "var(--text-2)",
                          borderRadius: 2,
                          fontSize: 13,
                        }}
                        data-testid={`trait-bucket-${k}-${it.value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <span>{it.value}</span>
                    <span className="font-mono text-[10px] opacity-70">{it.count}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] p-4"
         style={accent ? { boxShadow: "inset 0 0 0 1px rgba(212,175,55,0.18)" } : undefined}>
      <div className="flex items-center justify-between mb-1">
        <span className="label-eyebrow" style={{ color: "var(--text-muted)" }}>{label}</span>
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color: accent ? "var(--gold)" : "var(--text-muted)" }} />}
      </div>
      <div className="font-display text-3xl md:text-4xl tracking-tighter"
           style={{ color: accent ? "var(--gold)" : "var(--text)" }}>{value}</div>
    </div>
  );
}

/**
 * Radial classification wheel — sectors sized by record count.
 * Built with pure SVG, deterministic layout.
 */
function ClassificationWheel({ data }) {
  const items = (data.classification || []).filter((c) => c.value);
  const total = items.reduce((s, x) => s + x.count, 0);
  const size = 320;
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = 140;
  const rInner = 78;

  let cursor = -Math.PI / 2; // start at 12 o'clock

  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] p-5"
         data-testid="classification-wheel">
      <div className="flex items-center justify-between mb-4">
        <div className="label-eyebrow flex items-center gap-2" style={{ color: "var(--text-muted)" }}>
          <Hexagon className="w-3 h-3" style={{ color: "var(--gold)" }} /> Classification spectrum
        </div>
        <div className="text-[11px] font-mono uppercase tracking-[0.18em]" style={{ color: "var(--text-muted)" }}>
          {total} records classified
        </div>
      </div>

      {total === 0 ? (
        <div className="text-center text-sm py-6" style={{ color: "var(--text-muted)" }}>
          Classification will populate as verified records carry a classification tag.
        </div>
      ) : (
        <div className="flex flex-col md:flex-row items-center gap-6">
          <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
            <circle cx={cx} cy={cy} r={rOuter + 6} fill="rgba(212,175,55,0.04)" />
            {items.map((it, i) => {
              const meta = CLASS_META[it.value] || { label: it.value, color: "#7F838B" };
              const slice = (it.count / total) * Math.PI * 2;
              const a0 = cursor;
              const a1 = cursor + slice;
              cursor = a1;
              const path = arcPath(cx, cy, rInner, rOuter, a0, a1);
              const midA = (a0 + a1) / 2;
              const lx = cx + Math.cos(midA) * (rOuter + 14);
              const ly = cy + Math.sin(midA) * (rOuter + 14);
              return (
                <g key={it.value}>
                  <path d={path} fill={meta.color} opacity={0.85} stroke="rgba(8,9,11,0.6)" strokeWidth={1} />
                  <text x={lx} y={ly} textAnchor={Math.cos(midA) > 0 ? "start" : "end"}
                        style={{ fontFamily: "var(--font-mono, monospace)", fontSize: 10, letterSpacing: "0.06em" }}
                        fill="rgba(244,245,247,0.7)">
                    {meta.label}
                  </text>
                </g>
              );
            })}
            <circle cx={cx} cy={cy} r={rInner - 2} fill="var(--bg-2)" />
            <text x={cx} y={cy - 4} textAnchor="middle"
                  style={{ fontFamily: "var(--font-display, serif)", fontSize: 26, letterSpacing: "-0.02em" }}
                  fill="var(--text)">{total}</text>
            <text x={cx} y={cy + 14} textAnchor="middle"
                  style={{ fontFamily: "var(--font-mono, monospace)", fontSize: 9, letterSpacing: "0.18em" }}
                  fill="rgba(244,245,247,0.45)">CLASSIFIED</text>
          </svg>

          <div className="flex-1 grid grid-cols-2 gap-2 w-full">
            {items.map((it) => {
              const meta = CLASS_META[it.value] || { label: it.value, color: "#7F838B" };
              const slug = _slugify(it.value);
              return (
                <Link key={it.value} to={`/classifications/${slug}`}
                      className="flex items-center justify-between border border-[var(--border)] bg-[var(--bg-2)] px-3 py-2 text-xs hover:border-[var(--gold)]/60 transition-colors"
                      data-testid={`trait-class-${it.value}`}>
                  <span className="inline-flex items-center gap-2">
                    <span style={{ width: 9, height: 9, background: meta.color, borderRadius: 9999 }} />
                    <span style={{ color: "var(--text-2)" }}>{meta.label}</span>
                  </span>
                  <span className="font-mono" style={{ color: "var(--text)" }}>{it.count}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function arcPath(cx, cy, rInner, rOuter, a0, a1) {
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const x0o = cx + Math.cos(a0) * rOuter;
  const y0o = cy + Math.sin(a0) * rOuter;
  const x1o = cx + Math.cos(a1) * rOuter;
  const y1o = cy + Math.sin(a1) * rOuter;
  const x0i = cx + Math.cos(a1) * rInner;
  const y0i = cy + Math.sin(a1) * rInner;
  const x1i = cx + Math.cos(a0) * rInner;
  const y1i = cy + Math.sin(a0) * rInner;
  return `M${x0o} ${y0o} A${rOuter} ${rOuter} 0 ${large} 1 ${x1o} ${y1o} L${x0i} ${y0i} A${rInner} ${rInner} 0 ${large} 0 ${x1i} ${y1i} Z`;
}

function BarPanel({ title, icon: Icon, items, unit, testid }) {
  const max = Math.max(1, ...((items || []).map((x) => x.count)));
  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div className="label-eyebrow flex items-center gap-2" style={{ color: "var(--text-muted)" }}>
          {Icon && <Icon className="w-3 h-3" style={{ color: "var(--gold)" }} />} {title}
        </div>
        <div className="text-[11px] font-mono uppercase tracking-[0.18em]" style={{ color: "var(--text-muted)" }}>
          {(items || []).reduce((s, x) => s + x.count, 0)} {unit}
        </div>
      </div>
      {(!items || items.length === 0) ? (
        <div className="text-center text-xs py-4" style={{ color: "var(--text-muted)" }}>No records yet.</div>
      ) : (
        <ul className="space-y-2">
          {items.map((it) => (
            <li key={it.value} className="text-xs">
              <div className="flex justify-between mb-1">
                <span style={{ color: "var(--text-2)" }}>{it.value.replace(/_/g, " ")}</span>
                <span className="font-mono" style={{ color: "var(--text)" }}>{it.count}</span>
              </div>
              <div className="w-full h-1.5 bg-[var(--bg-2)]" style={{ borderRadius: 2 }}>
                <div className="h-full" style={{
                  width: `${(it.count / max) * 100}%`,
                  background: "linear-gradient(90deg, #4FA86F, #D4AF37)",
                  borderRadius: 2,
                }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PreservationCard({ flags, total }) {
  const items = [
    { key: "landrace", label: "Landrace", icon: Mountain, count: flags.landrace },
    { key: "heirloom", label: "Heirloom", icon: ShieldCheck, count: flags.heirloom },
    { key: "preservation", label: "Preservation", icon: Sparkles, count: flags.preservation },
  ];
  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] p-5" data-testid="trait-preservation">
      <div className="label-eyebrow mb-4 flex items-center gap-2" style={{ color: "var(--text-muted)" }}>
        <ShieldCheck className="w-3 h-3" style={{ color: "var(--gold)" }} /> Preservation flags
      </div>
      <div className="grid grid-cols-3 gap-3">
        {items.map(({ key, label, icon: Icon, count }) => {
          const pct = total ? Math.round((count / total) * 100) : 0;
          return (
            <div key={key} className="border border-[var(--border)] bg-[var(--bg-2)] p-3 text-center">
              <Icon className="w-4 h-4 mx-auto mb-2" style={{ color: "var(--gold)" }} />
              <div className="font-display text-2xl" style={{ color: "var(--text)" }}>{count}</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.16em] mt-1" style={{ color: "var(--text-muted)" }}>{label}</div>
              <div className="text-[10px] mt-1" style={{ color: "var(--text-muted)" }}>{pct}% of mapped</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// v3.80 — TagConstellation removed; replaced by curated `TraitBuckets`.

/**
 * TerpenePanel — v3.89i
 * Surface the terpenes layer pulled from Demarily auto-enrichment (and any
 * admin-entered values). Honest framing: this is third-party metadata, not
 * a lab assay on the breeder's own pheno.
 */
function TerpenePanel({ data }) {
  const items = data.terpenes || [];
  const enrichedCount = data.demarily_enriched_count || 0;
  if (!items.length && !enrichedCount) return null;
  const max = Math.max(1, ...items.map((x) => x.count));
  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] p-5" data-testid="trait-terpene-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="label-eyebrow flex items-center gap-2" style={{ color: "var(--text-muted)" }}>
          <Database className="w-3 h-3" style={{ color: "var(--gold)" }} /> Terpene profile
        </div>
        <div className="text-[10px] font-mono uppercase tracking-[0.16em]" style={{ color: "var(--text-muted)" }}>
          {enrichedCount} record{enrichedCount === 1 ? "" : "s"} enriched · Demarily attribution
        </div>
      </div>
      {items.length === 0 ? (
        <div className="text-center text-xs py-4" style={{ color: "var(--text-muted)" }}>
          Terpene chips will populate as enriched records come in.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {items.map((it) => {
            const ratio = it.count / max;
            const slug = _slugify(it.value);
            return (
              <Link key={it.value}
                    to={`/terpenes/${slug}`}
                    className="inline-flex items-baseline gap-1.5 px-3 py-1.5 border transition-colors hover:bg-[var(--bg-2)]"
                    style={{
                      borderColor: ratio >= 0.6 ? "var(--gold)" : "var(--border)",
                      color: ratio >= 0.6 ? "var(--gold)" : "var(--text-2)",
                      borderRadius: 2,
                      fontSize: 13,
                    }}
                    data-testid={`trait-terpene-${it.value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <span>{it.value}</span>
                <span className="font-mono text-[10px] opacity-70">{it.count}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}



/**
 * TraitNavHub — v3.99c.40
 * Listing-aware navigation hub. Renders all 7 trait-page types as a clickable
 * grid of chips. Each chip shows record count + listing count. Live commercial
 * intent (listings > 0) is visually highlighted. Includes filters/sorts so an
 * admin or collector can find the most-shoppable categories fast.
 */
const NAV_HUB_META = {
  families:        { Icon: GitBranch, label: "Families" },
  terpenes:        { Icon: Beaker,    label: "Terpenes" },
  aromas:          { Icon: Wind,      label: "Aromas" },
  classifications: { Icon: Layers,    label: "Classifications" },
  seeds:           { Icon: Sprout,    label: "Seed types" },
  preservation:    { Icon: ShieldCheck, label: "Preservation" },
  structures:      { Icon: Hexagon,   label: "Plant structures" },
};

function TraitNavHub({ matrix }) {
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("commercial");
  const sections = useMemo(() => {
    const keys = ["families", "classifications", "terpenes", "aromas", "seeds", "preservation", "structures"];
    return keys.filter((k) => matrix[k]).map((k) => {
      let items = (matrix[k]?.items || []).slice();
      if (filter === "listings") items = items.filter((i) => i.listings > 0);
      if (filter === "indexable") items = items.filter((i) => i.indexable);
      if (sortBy === "strains") items.sort((a, b) => b.strains - a.strains);
      if (sortBy === "alpha")   items.sort((a, b) => a.name.localeCompare(b.name));
      if (sortBy === "commercial") items.sort((a, b) => (b.listings - a.listings) || (b.strains - a.strains));
      return { key: k, ...NAV_HUB_META[k], items };
    });
  }, [matrix, filter, sortBy]);

  const totalShoppable = useMemo(() => {
    let n = 0;
    Object.values(matrix || {}).forEach((sec) =>
      (sec.items || []).forEach((i) => { if (i.listings > 0) n += i.listings; }));
    return n;
  }, [matrix]);

  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] p-5" data-testid="trait-nav-hub">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="label-eyebrow mb-1 inline-flex items-center gap-1.5"
               style={{ color: "var(--gold)" }}>
            <ShoppingBag className="w-3 h-3" /> Trait navigation · {totalShoppable} live listings across categories
          </div>
          <h3 className="font-display text-xl md:text-2xl tracking-tighter" style={{ color: "var(--text)" }}>
            Click any trait to open its landing page
          </h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <FilterChip active={filter === "all"}        onClick={() => setFilter("all")}        label="All" testid="trait-nav-filter-all" />
          <FilterChip active={filter === "indexable"}  onClick={() => setFilter("indexable")}  label="Has records" testid="trait-nav-filter-indexable" />
          <FilterChip active={filter === "listings"}   onClick={() => setFilter("listings")}   label="Has listings" testid="trait-nav-filter-listings" />
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                  className="text-[11px] font-mono uppercase tracking-[0.14em] border border-[var(--border)] bg-[var(--bg-2)] px-2 py-1"
                  style={{ color: "var(--text-2)", borderRadius: 2 }}
                  data-testid="trait-nav-sort">
            <option value="commercial">sort: commercial</option>
            <option value="strains">sort: most strains</option>
            <option value="alpha">sort: A-Z</option>
          </select>
        </div>
      </div>

      <div className="space-y-4">
        {sections.map(({ key, Icon, label, items }) => (
          <div key={key} data-testid={`trait-nav-section-${key}`}>
            <div className="label-eyebrow mb-2 inline-flex items-center gap-1.5"
                 style={{ color: "var(--text-muted)" }}>
              <Icon className="w-3 h-3" style={{ color: "var(--gold)" }} /> {label}
            </div>
            {items.length === 0 ? (
              <div className="text-xs opacity-50 px-1 py-2" style={{ color: "var(--text-muted)" }}>
                No {label.toLowerCase()} match the current filter.
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {items.map((it) => (
                  <Link key={it.slug} to={it.url}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border transition-colors hover:bg-[var(--bg-2)]"
                        style={{
                          borderColor: it.listings > 0 ? "var(--gold)" : "var(--border)",
                          color: it.listings > 0 ? "var(--gold)" : (it.indexable ? "var(--text)" : "var(--text-muted)"),
                          borderRadius: 2,
                          fontSize: 12,
                          opacity: it.indexable ? 1 : 0.6,
                        }}
                        title={`${it.strains} strain${it.strains === 1 ? "" : "s"} · ${it.listings} listing${it.listings === 1 ? "" : "s"}`}
                        data-testid={`trait-nav-chip-${key}-${it.slug}`}>
                    <span>{it.name}</span>
                    <span className="font-mono text-[10px] opacity-70">{it.strains}</span>
                    {it.listings > 0 && (
                      <span className="font-mono text-[9px] px-1"
                            style={{ background: "var(--gold)", color: "#000" }}
                            data-testid={`trait-nav-listing-badge-${key}-${it.slug}`}>
                        ●{it.listings}
                      </span>
                    )}
                    <ArrowRight className="w-2.5 h-2.5 opacity-50" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, label, testid }) {
  return (
    <button type="button" onClick={onClick}
            data-testid={testid}
            className="text-[11px] font-mono uppercase tracking-[0.14em] border px-2 py-1 transition-colors"
            style={{
              borderColor: active ? "var(--gold)" : "var(--border)",
              color: active ? "var(--gold)" : "var(--text-muted)",
              background: active ? "rgba(212,175,55,0.08)" : "transparent",
              borderRadius: 2,
            }}>
      {label}
    </button>
  );
}
